/**
 * @architecture-header 1
 * @layer app
 * @domain workspace
 * @responsibility Provide host-injected browser or desktop capabilities to authenticated modules.
 * @state-owner React provider immutable host configuration
 * @allowed-imports React; @vispo/api-client
 * @forbidden direct Vite env; Electron globals; module state
 * @public-api via @vispo/workspace-web
 * @side-effects context reads only
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 * @environment-scope multi-environment
 * @credential-scope runtime-selected
 * @destructive-action none
 */

import type { VispoApiClient } from '@vispo/api-client';
import { createContext, type PropsWithChildren, useContext } from 'react';
import type { SeatStatus } from '../../shared/lib/model';

export type WorkspaceAuthProvider = 'email' | 'google';

export interface WorkspaceSession {
  userId: string;
  email: string;
  displayName: string | null;
  avatarUrl?: string | null;
  /** Host-stamped, session-scoped portal that authenticated this identity. */
  portalAudience?: 'student' | 'teacher';
  /** Login methods currently linked to this identity; display-only (SET-F02). */
  linkedProviders: readonly WorkspaceAuthProvider[];
}

export type WorkspaceAuthErrorKind = 'unknown-account' | 'retryable';

export class WorkspaceAuthError extends Error {
  readonly kind: WorkspaceAuthErrorKind;
  readonly retryable: boolean;

  constructor(kind: WorkspaceAuthErrorKind) {
    super(
      kind === 'unknown-account' ? 'Vispo account was not found' : 'Authentication provider error',
    );
    this.name = 'WorkspaceAuthError';
    this.kind = kind;
    this.retryable = kind === 'retryable';
  }
}

export interface WorkspaceAuthAdapter {
  getSession(): Promise<WorkspaceSession | null>;
  subscribe(listener: (session: WorkspaceSession | null) => void): () => void;
  sendEmailCode(credentials: { email: string }): Promise<void>;
  /** Strict-login resend; registration deliberately uses a distinct provider operation. */
  resendEmailCode?(credentials: { email: string }): Promise<void>;
  verifyEmailCode(credentials: { email: string; token: string }): Promise<void>;
  signInWithGoogle(options?: { redirectPath?: string }): Promise<void>;
  /** Stamp the initiating portal; hosts retain it per user for the persisted auth-session lifetime. */
  setPortalAudience?(audience: 'student' | 'teacher'): Promise<void>;
  /** Registration is deliberately distinct from strict-login provider calls. */
  sendRegistrationCode?(credentials: { email: string }): Promise<void>;
  verifyRegistrationCode?(credentials: { email: string; token: string }): Promise<void>;
  registerWithGoogle?(options?: { redirectPath?: string }): Promise<void>;
  /** Client-only email change (SET-F02 R2); the provider owns confirmation, never a backend op. */
  requestEmailChange(newEmail: string): Promise<void>;
  signOut(): Promise<void>;
}

export interface WorkspacePresenceRecord {
  userId: string;
  studentName: string;
  /** The one seat vocabulary the product speaks; hosts translate into it, never past it. */
  status: SeatStatus;
  examId?: string;
  examTitle?: string;
  phase?: string;
  currentQuestionIndex?: number;
  totalQuestions?: number;
  confusionAlerts?: Array<{
    type: 'stuck' | 'modified' | 'manual';
    questionIndex: number;
    timestamp: string;
  }>;
  lastActiveAt: string;
}

export interface WorkspaceRealtimeAdapter {
  subscribeClassPresence(
    classId: string,
    listener: (snapshot: { connected: boolean; records: WorkspacePresenceRecord[] }) => void,
  ): Promise<() => void>;
  trackClassPresence(classId: string, record: WorkspacePresenceRecord): Promise<() => void>;
}

export interface WorkspaceTransientNavigationAdapter {
  /** Register a host-owned temporary Back-stack entry and release it when the surface closes. */
  open(key: string, onBack: () => void): () => void;
}

/** Development-only seam supplied by the local E2E driver; production hosts omit it. */
export interface WorkspaceTestAdapter {
  read(name: string): unknown;
  notify(name: string): void;
}

export interface WorkspaceEnvironment {
  apiClient: VispoApiClient;
  auth: WorkspaceAuthAdapter;
  realtime: WorkspaceRealtimeAdapter;
  transientNavigation?: WorkspaceTransientNavigationAdapter;
  testAdapter?: WorkspaceTestAdapter;
  platform: 'browser' | 'desktop';
  demoMode: boolean;
  /** Local E2E can enable the production portal-audience gate while retaining demo data. */
  enforcePortalAudience?: boolean;
  openExternal(url: string): Promise<void>;
}

const EnvironmentContext = createContext<WorkspaceEnvironment | null>(null);

export function EnvironmentProvider({
  children,
  environment,
}: PropsWithChildren<{ environment: WorkspaceEnvironment }>) {
  return <EnvironmentContext.Provider value={environment}>{children}</EnvironmentContext.Provider>;
}

export function useWorkspaceEnvironment(): WorkspaceEnvironment {
  const value = useContext(EnvironmentContext);
  if (!value) throw new Error('WorkspaceEnvironment is missing');
  return value;
}

export function useOptionalWorkspaceEnvironment(): WorkspaceEnvironment | null {
  return useContext(EnvironmentContext);
}
